import { Instagram, Loader2, RefreshCw } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
const PhotoBook = () => {
  const instagramHandle = "renewsantulan";
  const instagramUrl = `https://instagram.com/${instagramHandle}`;
  const queryClient = useQueryClient();
  const {
    data: reels,
    isLoading,
    error
  } = useQuery({
    queryKey: ['instagram-reels'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('instagram_reels').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      return data;
    }
  });
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const {
        data,
        error
      } = await supabase.functions.invoke('scrape-instagram-reels', {
        body: {
          refresh: false
        }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['instagram-reels']
      });
      toast.success('Instagram reels refreshed successfully!');
    },
    onError: error => {
      console.error('Refresh error:', error);
      toast.error('Failed to refresh reels. Please try again later.');
    }
  });
  return <section className="section-padding container-custom bg-secondary/30">
      <div className="space-y-8 sm:space-y-12">
        <div className="text-center space-y-3 sm:space-y-4 px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground">
            Follow My Journey
          </h2>
          <div className="flex flex-col items-center gap-3">
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-base sm:text-lg text-accent hover:text-accent/80 transition-colors min-h-[44px]">
              <Instagram className="w-5 h-5" />
              @{instagramHandle}
            </a>
            
          </div>
        </div>

        {isLoading && <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>}

        {error && <div className="text-center py-12">
            <p className="text-muted-foreground">
              Unable to load Instagram posts. Please try again later.
            </p>
          </div>}

        {!isLoading && !error && reels && reels.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6 max-w-7xl mx-auto px-4 sm:px-6">
            {reels.slice(0, 8).map(reel => <a key={reel.id} href={reel.post_url} target="_blank" rel="noopener noreferrer" className="group">
                <Card className="aspect-[9/16] overflow-hidden hover:shadow-xl transition-all duration-300 border-border/50 bg-muted/20">
                  <div className="relative w-full h-full flex items-center justify-center">
                    <img src={reel.stored_thumbnail_url || reel.thumbnail_url} alt={reel.caption || "Instagram post"} className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" onError={e => {
                const target = e.target as HTMLImageElement;
                target.src = reel.thumbnail_url;
              }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between">
                        <Instagram className="w-6 h-6 text-white" />
                        {reel.is_video && <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                            <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z" />
                          </svg>}
                      </div>
                    </div>
                  </div>
                </Card>
              </a>)}
          </div>}

        {!isLoading && !error && reels && reels.length === 0 && <div className="text-center py-12">
            <p className="text-muted-foreground">
              No posts available yet. Check back soon!
            </p>
          </div>}

        <div className="text-center px-4">
          <p className="text-sm sm:text-base text-muted-foreground">
            Click any image to view the post on Instagram
          </p>
        </div>
      </div>
    </section>;
};
export default PhotoBook;